/**
 * use-ai-khung.ts — Lệnh gọi API MỚI của module Nhân viên AI theo khung ba tầng (docs/KE-HOACH-KHUNG-AI-3-TANG.md §5),
 * gom một chỗ để dễ sửa khi hợp đồng backend chốt:
 *  - GET /ai/library (dự phòng: /ai/logic-docs/:type + /ai/scenarios khi server chưa có);
 *  - PUT /ai/scenarios/:id { botIds } (phạm vi kỹ năng);
 *  - PUT /ai/config { autoReplyEnabled } ("Tạm dừng toàn bộ AI", nhãn đảo nghĩa) / { enabled: true } (bật lại AI).
 * (Chế độ AI của hội thoại — `aiMode: 'inherit'` — nằm ở `useSetAiMode` trong use-conversations.ts.)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { isApiMissing } from '@/lib/ai-credits'
import { aiKeys, updateAiConfig, type ScenarioMeta } from '@/hooks/use-ai'
import { i18n } from '@/lib/i18n'

/** Quy tắc an toàn chung — áp cho mọi nhân viên AI (trừ nhân viên đã tắt "Dùng quy tắc chung"). */
export const SHARED_RULE_TYPES = ['criteria', 'handoff_rules', 'index', 'mechanism'] as const
/** Mẫu nhân vật / quy trình — để chép vào nhân viên AI, không tự áp dụng. */
export const TEMPLATE_TYPES = ['persona', 'playbook'] as const

export type SharedRuleType = (typeof SHARED_RULE_TYPES)[number]
export type TemplateType = (typeof TEMPLATE_TYPES)[number]

export const LIBRARY_DOC_LABEL: Record<SharedRuleType | TemplateType, { title: string; hint: string }> = {
  criteria: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.criteria.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.criteria.hint') } },
  handoff_rules: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.handoff_rules.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.handoff_rules.hint') } },
  index: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.index.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.index.hint') } },
  mechanism: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.mechanism.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.mechanism.hint') } },
  persona: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.persona.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.persona.hint') } },
  playbook: { get title() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.playbook.title') }, get hint() { return i18n.t('aiTrainLib:useAiKhung.libraryDoc.playbook.hint') } },
}

export interface LibraryDoc {
  type: string
  title: string
  content: string | null
}

export interface AiLibrary {
  sharedRules: LibraryDoc[]
  templates: { persona: string | null; playbook: string | null }
  scenarios: ScenarioMeta[]
  /** 'library' = GET /ai/library; 'legacy' = ghép từ API cũ (server chưa có /ai/library). */
  origin: 'library' | 'legacy'
}

export const aiLibraryKey = ['ai', 'library'] as unknown[]

interface LibraryResponse {
  sharedRules?: Array<{ type: string; title?: string | null; content?: string | null }>
  templates?: { persona?: string | null; playbook?: string | null } | null
  scenarios?: ScenarioMeta[]
}

/** Server trả tài liệu dạng chuỗi hoặc { content } — nhận cả hai. */
function docContent(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object' && 'content' in v) {
    const c = (v as { content?: unknown }).content
    return typeof c === 'string' ? c : null
  }
  return null
}

function normalizeLibrary(data: LibraryResponse): AiLibrary {
  const byType = new Map((data.sharedRules ?? []).map((d) => [d.type, d]))
  return {
    sharedRules: SHARED_RULE_TYPES.map((type) => ({
      type,
      title: byType.get(type)?.title || LIBRARY_DOC_LABEL[type].title,
      content: byType.get(type)?.content ?? null,
    })),
    templates: {
      persona: docContent(data.templates?.persona),
      playbook: docContent(data.templates?.playbook),
    },
    scenarios: data.scenarios ?? [],
    origin: 'library',
  }
}

async function fetchDoc(type: string): Promise<string | null> {
  try {
    const { data } = await api.get<{ doc?: { content?: string } | null }>(`/ai/logic-docs/${type}`)
    return data.doc?.content ?? null
  } catch (err) {
    // 404 = tổ chức chưa có tài liệu này; 400 = server cũ chưa nhận loại tài liệu (vd criteria).
    const s = (err as { response?: { status?: number } })?.response?.status
    if (s === 404 || s === 400) return null
    throw err
  }
}

async function fetchLegacyLibrary(): Promise<AiLibrary> {
  const [docs, sc] = await Promise.all([
    Promise.all([...SHARED_RULE_TYPES, ...TEMPLATE_TYPES].map(async (t) => [t, await fetchDoc(t)] as const)),
    api.get<{ scenarios: ScenarioMeta[] }>('/ai/scenarios'),
  ])
  const map = new Map(docs)
  return {
    sharedRules: SHARED_RULE_TYPES.map((type) => ({ type, title: LIBRARY_DOC_LABEL[type].title, content: map.get(type) ?? null })),
    templates: { persona: map.get('persona') ?? null, playbook: map.get('playbook') ?? null },
    scenarios: sc.data.scenarios ?? [],
    origin: 'legacy',
  }
}

/** Thư mục dùng chung: GET /ai/library; server chưa có (404/405/501) → ghép từ API cũ. */
export function useAiLibrary() {
  return useQuery<AiLibrary>({
    queryKey: aiLibraryKey,
    queryFn: async () => {
      try {
        const { data } = await api.get<LibraryResponse>('/ai/library')
        return normalizeLibrary(data)
      } catch (err) {
        if (isApiMissing(err)) return fetchLegacyLibrary()
        throw err
      }
    },
    retry: false,
    staleTime: 30_000,
  })
}

export function invalidateLibrary(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: aiLibraryKey }),
    qc.invalidateQueries({ queryKey: aiKeys.scenarios }),
  ])
}

/** Sửa một tài liệu của thư viện (quy tắc an toàn chung / mẫu) — có hiệu lực ngay. */
export function useSaveLibraryDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: { type: string; content: string }) => {
      const { data } = await api.put(`/ai/logic-docs/${v.type}`, { content: v.content, changeNote: 'Sửa từ Thư mục dùng chung' }) // i18n-ignore (dữ liệu: ghi chú thay đổi lưu vào lịch sử tài liệu)
      return data
    },
    onSuccess: () => invalidateLibrary(qc),
  })
}

/** Đổi phạm vi kỹ năng: null = mọi nhân viên AI, mảng id = chỉ các nhân viên đó. */
export function useSetScenarioBots() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: { id: string; botIds: string[] | null }) => {
      const { data } = await api.put(`/ai/scenarios/${v.id}`, { botIds: v.botIds })
      return data as { scenario?: ScenarioMeta }
    },
    onSuccess: () => invalidateLibrary(qc),
  })
}

/**
 * "Tạm dừng toàn bộ AI" = `autoReplyEnabled=false` (nhãn đảo nghĩa). Tạm dừng: mọi hội thoại về Thủ công, không nhân viên
 * AI nào trả lời / gợi ý. Bỏ tạm dừng: nhân viên AI chạy lại theo cài đặt riêng của từng người.
 */
export function useSetAiPausedAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (paused: boolean) => updateAiConfig({ autoReplyEnabled: !paused }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: aiKeys.config }),
      qc.invalidateQueries({ queryKey: aiKeys.bots }),
    ]),
  })
}

/** Bật lại AI của tổ chức (`enabled=true`) — khi đang tắt từ cấu hình cũ. */
export function useEnableOrgAi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => updateAiConfig({ enabled: true }),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: aiKeys.config }),
      qc.invalidateQueries({ queryKey: aiKeys.bots }),
    ]),
  })
}
